import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Rail } from "../components/Rail";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Field, inputCls, btnMint, btnGhost } from "../lib/ui";
import { Switch } from "../components/ui/switch";

export default function SettingsPage() {
  const { account, setAccount, refreshAccount } = useAuth();
  const [form, setForm] = useState(null);
  const [headersText, setHeadersText] = useState("");

  useEffect(() => {
    if (account && !form) {
      setForm({
        salesperson_name: account.salesperson_name || "",
        company_name: account.company_name || "",
        ai_enabled: account.ai_enabled !== false,
        schedule_mode: account.schedule_mode || "any_time",
        schedule_start: account.schedule_start || "09:00",
        schedule_end: account.schedule_end || "17:00",
        webhook_url: account.webhook_url || "",
        webhook_method: account.webhook_method || "POST",
        webhook_events: account.webhook_events || [],
      });
      setHeadersText(account.webhook_headers && Object.keys(account.webhook_headers).length
        ? JSON.stringify(account.webhook_headers, null, 2) : "");
    }
  }, [account, form]);

  const toggleEvent = (ev) => {
    const list = form.webhook_events.includes(ev)
      ? form.webhook_events.filter((x) => x !== ev)
      : [...form.webhook_events, ev];
    setForm({ ...form, webhook_events: list });
  };

  const save = async (e) => {
    e.preventDefault();
    let webhook_headers = {};
    if (headersText.trim()) {
      try {
        webhook_headers = JSON.parse(headersText);
      } catch {
        toast.error("Headers harus berupa JSON valid, contoh: {\"Authorization\": \"Bearer xxx\"}");
        return;
      }
    }
    try {
      const { data } = await api.put("/settings", { ...form, webhook_headers });
      setAccount(data);
      toast.success("Pengaturan disimpan");
    } catch (err) { toast.error(apiError(err)); }
  };

  const disconnect = async () => {
    await api.post("/whatsapp/disconnect");
    await refreshAccount();
    toast.success("WhatsApp diputus");
  };

  if (!form) return null;

  return (
    <div className="flex h-screen bg-ink text-paper">
      <Rail />
      <main className="flex-1 overflow-y-auto">
        <header className="sticky top-0 z-10 border-b border-white/5 bg-ink/80 px-8 py-5 backdrop-blur-md">
          <h1 className="text-xl font-semibold">Pengaturan</h1>
          <p className="text-sm text-muted">Identitas AI, jadwal respons, dan koneksi WhatsApp</p>
        </header>
        <form onSubmit={save} className="max-w-7xl p-8">
          <div className="grid items-start gap-8 xl:grid-cols-2">
            <div className="space-y-8">
          <section className="rounded-2xl border border-white/5 bg-panel p-6">
            <h2 className="text-base font-semibold md:text-lg">Identitas Sales AI</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <Field label="Nama Salesperson AI">
                <input data-testid="settings-salesperson-input" className={inputCls} value={form.salesperson_name}
                  onChange={(e) => setForm({ ...form, salesperson_name: e.target.value })} />
              </Field>
              <Field label="Nama Perusahaan">
                <input data-testid="settings-company-input" className={inputCls} value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
              </Field>
            </div>
          </section>

          <section className="rounded-2xl border border-white/5 bg-panel p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold md:text-lg">AI Auto-Reply</h2>
                <p className="text-sm text-muted">Matikan untuk menghentikan semua balasan AI</p>
              </div>
              <Switch data-testid="settings-ai-switch" checked={form.ai_enabled}
                onCheckedChange={(v) => setForm({ ...form, ai_enabled: v })} className="data-[state=checked]:bg-mint" />
            </div>
            <div className="mt-6 space-y-4">
              <div className="inline-flex rounded-full border border-white/10 bg-ink p-1">
                {[["any_time", "Kapan saja"], ["only_selected_times", "Jam tertentu"]].map(([v, l]) => (
                  <button type="button" key={v} data-testid={`settings-schedule-${v}`} onClick={() => setForm({ ...form, schedule_mode: v })}
                    className={`rounded-full px-4 py-1.5 text-sm transition-colors ${form.schedule_mode === v ? "bg-paper font-semibold text-ink" : "text-muted hover:text-paper"}`}>
                    {l}
                  </button>
                ))}
              </div>
              {form.schedule_mode === "only_selected_times" && (
                <div className="flex items-center gap-3">
                  <input data-testid="settings-schedule-start" type="time" className={`${inputCls} w-32 font-mono`} value={form.schedule_start}
                    onChange={(e) => setForm({ ...form, schedule_start: e.target.value })} />
                  <span className="text-muted">sampai</span>
                  <input data-testid="settings-schedule-end" type="time" className={`${inputCls} w-32 font-mono`} value={form.schedule_end}
                    onChange={(e) => setForm({ ...form, schedule_end: e.target.value })} />
                  <span className="font-mono text-xs text-faint">WIB</span>
                </div>
              )}
            </div>
          </section>

          <section className="rounded-2xl border border-white/5 bg-panel p-6">
            <h2 className="text-base font-semibold md:text-lg">Koneksi WhatsApp</h2>
            <div className="mt-3 flex items-center justify-between">
              <div>
                <p className="font-mono text-sm">{account?.phone || "-"}</p>
                <p className={`text-xs ${account?.wa_status === "connected" ? "text-mint" : "text-faint"}`}>
                  {account?.wa_status === "connected" ? "Terhubung" : "Tidak terhubung"}
                </p>
              </div>
              {account?.wa_status === "connected" ? (
                <button type="button" data-testid="settings-disconnect-btn" onClick={disconnect} className={btnGhost}>Putuskan</button>
              ) : (
                <a href="/onboarding" data-testid="settings-connect-link" className={btnGhost}>Hubungkan</a>
              )}
            </div>
          </section>
            </div>

          <section className="rounded-2xl border border-white/5 bg-panel p-6">
            <h2 className="text-base font-semibold md:text-lg">API Integrasi (Webhook Keluar)</h2>
            <p className="mt-1 text-sm text-muted">
              Setiap ada data lead baru atau perubahan data lead, ChatSensei mengirim data JSON ke endpoint Anda — cocok untuk sinkronisasi CRM.
            </p>
            <div className="mt-5 space-y-4">
              <div className="flex gap-3">
                <div className="w-32">
                  <span className="mb-1.5 block text-xs font-medium text-muted">Method</span>
                  <select data-testid="settings-webhook-method" value={form.webhook_method}
                    onChange={(e) => setForm({ ...form, webhook_method: e.target.value })}
                    className={`${inputCls} font-mono`}>
                    {["POST", "PUT", "PATCH"].map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div className="flex-1">
                  <Field label="URL Endpoint">
                    <input data-testid="settings-webhook-url" type="url" placeholder="https://api.crm-anda.com/leads"
                      className={`${inputCls} font-mono`} value={form.webhook_url}
                      onChange={(e) => setForm({ ...form, webhook_url: e.target.value })} />
                  </Field>
                </div>
              </div>
              <Field label='Headers (JSON, opsional — contoh: {"Authorization": "Bearer xxx"})'>
                <textarea data-testid="settings-webhook-headers" rows={3} placeholder='{"Authorization": "Bearer xxx"}'
                  className={`${inputCls} font-mono text-xs`} value={headersText}
                  onChange={(e) => setHeadersText(e.target.value)} />
              </Field>
              <div>
                <span className="mb-2 block text-xs font-medium text-muted">Event yang dikirim ke endpoint</span>
                <div className="space-y-2.5">
                  {[["lead.created", "Lead baru dibuat"],
                    ["lead.updated", "Data lead berubah (interest, stage, handoff, revenue)"]].map(([ev, label]) => (
                    <label key={ev} className="flex cursor-pointer items-center gap-3 text-sm">
                      <input data-testid={`settings-webhook-event-${ev}`} type="checkbox" className="h-4 w-4 accent-[#4ade7c]"
                        checked={form.webhook_events.includes(ev)} onChange={() => toggleEvent(ev)} />
                      <span className="font-mono text-xs text-mint">{ev}</span>
                      <span className="text-muted">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </section>
          </div>

          <button data-testid="settings-save-btn" className={btnMint + " mt-8"}>Simpan Pengaturan</button>
        </form>
      </main>
    </div>
  );
}
